import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from '@tanstack/react-router';

import { usePresence } from '../lib/motion/hooks';

type AccountMenuProps = {
  name: string;
  roleWord: string;
  initials: string;
  /** The owner's plan ("Max"); the team never sees it. */
  planLabel?: string;
  onLogout: () => void;
  /** Where the menu opens: up from the sidebar foot (default) or down from the Helios top bar. */
  placement?: 'up' | 'down';
};

/**
 * The profile at the foot of the sidebar, the way ChatGPT and Claude do it:
 * one tap opens the account's own places (the plan, Configuración, Salir)
 * so they stop crowding the work in the nav.
 */
export function AccountMenu({ name, roleWord, initials, planLabel, onLogout, placement = 'up' }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const { mounted, phase } = usePresence(open, { exitMs: 120 });
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  // Opening moves focus into the menu; a click anywhere else closes it.
  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    moveFocus(menuRef.current, event.key === 'ArrowDown' ? 1 : -1);
  };

  return (
    <div className="account" data-placement={placement} ref={rootRef}>
      {mounted ? (
        <div className="account-menu" data-phase={phase} inert={phase === 'closing'} aria-hidden={phase === 'closing' || undefined} id={menuId} role="menu" aria-label="Tu cuenta" ref={menuRef} onKeyDown={onMenuKeyDown}>
          <div className="account-menu-head" role="presentation">
            <p className="account-menu-name">{name}</p>
            <p className="account-menu-role">{roleWord}</p>
          </div>
          {planLabel ? (
            <Link to="/membresia" role="menuitem" className="account-menu-item" onClick={() => close(false)}>
              <span>Tu plan</span>
              <span className="account-menu-hint">{planLabel}</span>
            </Link>
          ) : null}
          <Link to="/configuracion" role="menuitem" className="account-menu-item" onClick={() => close(false)}>
            Configuración
          </Link>
          <div className="account-menu-rule" role="separator" />
          <button type="button" role="menuitem" className="account-menu-item" onClick={() => { close(false); onLogout(); }}>
            Salir
          </button>
        </div>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        className="account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="account-avatar" aria-hidden="true">{initials}</span>
        <span className="account-text">
          <span className="account-name">{name}</span>
          <span className="account-sub">{planLabel ? `${roleWord} · Plan ${planLabel}` : roleWord}</span>
        </span>
        <svg className="account-chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M4.5 10 8 6.5l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
}

/** Arrow keys walk the items and wrap around, like a native menu. */
function moveFocus(menu: HTMLElement | null, step: 1 | -1): void {
  const items = Array.from(menu?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
  if (items.length === 0) return;
  const current = items.indexOf(document.activeElement as HTMLElement);
  const next = (current + step + items.length) % items.length;
  items[next]?.focus();
}
